import React, { Component, useEffect, useCallback } from "react";
import "./styles.css";
import { state, toggleMusic } from "../store.js";
import { useSnapshot } from "valtio";

let exportedAudioRef = null;

const AudioButton = ({ url }) => {
  const { audioVolume, finishedMainLoading, isMusicPlaying, isDraggingCamera } =
    useSnapshot(state);

  const playHover = () => {
    // Play hover sound effect if needed
  };
  const playClick = () => {
    // Play click sound effect if needed
  };

  const [playing, setPlaying] = React.useState(false);

  const audioEl = React.useRef(null);
  exportedAudioRef = audioEl;

  const onPlay = () => {
    setPlaying(true);
    toggleMusic(true);
  };
  const onPause = () => {
    setPlaying(false);
    toggleMusic(false);
  };
  const onEnded = () => {
    setPlaying(false);
    toggleMusic(false);
  };

  const playAudio = () => {
    if (audioEl.current) {
      audioEl.current.play();
      audioEl.current.volume = audioVolume;
      audioEl.current.loop = true;
    }
  };

  useEffect(() => {
    if (audioEl.current) {
      audioEl.current.volume = audioVolume;
    }
  }, [audioVolume]);

  const pauseAudio = () => {
    if (audioEl.current) {
      audioEl.current.pause();
    }
  };

  const startAudio = () => {
    playAudio();
  };

  // Keep track of window focus state
  const [wasMusicPlaying, setWasMusicPlaying] = React.useState(false);

  const handleFocus = useCallback(() => {
    // If music was playing when window lost focus, resume playback
    if (wasMusicPlaying) {
      audioEl.current.play();
    }
  }, [wasMusicPlaying]);

  const handleBlur = useCallback(() => {
    // Don't change global state, just pause audio
    const isPlaying = !audioEl.current.paused;
    setWasMusicPlaying(isPlaying);

    if (isPlaying) {
      audioEl.current.pause();
    }
  }, []);

  // Sync local state with global state
  useEffect(() => {
    setPlaying(isMusicPlaying);
  }, [isMusicPlaying]);

  // Sync audio element state with global state
  useEffect(() => {
    if (audioEl.current) {
      if (isMusicPlaying && audioEl.current.paused) {
        audioEl.current.play();
      } else if (!isMusicPlaying && !audioEl.current.paused) {
        audioEl.current.pause();
      }
    }
  }, [isMusicPlaying]);

  useEffect(() => {
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, [handleFocus, handleBlur]);

  const renderAudio = () => {
    const notSupportedMsg =
      "Your browser does not support the <code>audio</code> element.";
    return (
      <>
        <button
          id="buttonAudio"
          className={`btn ${isDraggingCamera ? "hidden" : ""}`}
          style={{
            display: !finishedMainLoading ? "none" : "flex",
          }}
          onClick={() => {
            !playing ? startAudio() : pauseAudio();
            playClick();
          }}
          onPointerEnter={playHover}
        >
          <svg
            id="wave"
            className={playing ? "animated" : ""}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 30 30"
          >
            <path
              id="Line_1"
              data-name="Line 1"
              fill="white"
              d="M0.91,15L0.78,15A1,1,0,0,0,0,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H0.91Z"
            />
            <path
              id="Line_2"
              data-name="Line 2"
              fill="white"
              d="M6.91,9L6.78,9A1,1,0,0,0,6,10V28a1,1,0,1,0,2,0s0,0,0,0V10A1,1,0,0,0,7,9H6.91Z"
            />
            <path
              id="Line_3"
              data-name="Line 3"
              fill="white"
              d="M12.91,0L12.78,0A1,1,0,0,0,12,1V37a1,1,0,1,0,2,0s0,0,0,0V1a1,1,0,0,0-1-1H12.91Z"
            />
            <path
              id="Line_4"
              data-name="Line 4"
              fill="white"
              d="M18.91,10l-0.12,0A1,1,0,0,0,18,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H18.91Z"
            />
            <path
              id="Line_5"
              data-name="Line 5"
              fill="white"
              d="M24.91,15l-0.12,0A1,1,0,0,0,24,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H24.91Z"
            />
          </svg>
        </button>

        <audio
          src={url}
          ref={audioEl}
          onPlay={onPlay}
          onPause={onPause}
          onEnded={onEnded}
        >
          {notSupportedMsg}
        </audio>
      </>
    );
  };

  return renderAudio();
};

export default AudioButton;

export { exportedAudioRef };
